import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface FavoritesContextType {
  favorites: string[];
  addFavorite: (carId: string) => void;
  removeFavorite: (carId: string) => void;
  isFavorite: (carId: string) => boolean;
  toggleFavorite: (carId: string) => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

const STORAGE_KEY = 'transshift-favorites';

// Helper function to update Airtable Saved_Count
const updateSavedCount = async (listingId: string, action: 'save' | 'unsave') => {
  try {
    const { error } = await supabase.functions.invoke('update-saved-count', {
      body: { listing_id: listingId, action }
    });
    if (error) {
      console.error('Failed to update saved count:', error);
    }
  } catch (err) {
    console.error('Error updating saved count:', err);
  }
};

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  }, [favorites]);

  const addFavorite = (carId: string) => {
    if (!favorites.includes(carId)) {
      setFavorites(prev => [...prev, carId]);
      // Fire and forget - don't block UI
      updateSavedCount(carId, 'save');
    }
  };

  const removeFavorite = (carId: string) => {
    if (favorites.includes(carId)) {
      setFavorites(prev => prev.filter(id => id !== carId));
      // Fire and forget - don't block UI
      updateSavedCount(carId, 'unsave');
    }
  };

  const isFavorite = (carId: string) => {
    return favorites.includes(carId);
  };

  const toggleFavorite = (carId: string) => {
    if (isFavorite(carId)) {
      removeFavorite(carId);
    } else {
      addFavorite(carId);
    }
  };

  return (
    <FavoritesContext.Provider value={{ favorites, addFavorite, removeFavorite, isFavorite, toggleFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within FavoritesProvider');
  }
  return context;
}
